import { useState, useEffect, useCallback } from "react";
import { EditPencil, FloppyDisk, Xmark, ArrowLeft } from "iconoir-react";
import "@knowtex-ai/components/style.css";
import {
  Badge, Button, Card, CardHeader, CardTitle, Heading, BodyText,
  FieldText, FieldTextarea, FieldSelect, Form, ConfirmationModal, Banner, LoadingAnimation,
} from "@knowtex-ai/components";
import { T, S, fmtDate } from "./theme";
import { fetchOutreachDetail, fetchOutreachUserFeedback, logOutreachAttempt, updateOutreachAttempt, removeOutreachCandidate } from "./api";
import { outreachStatusMetaFor } from "./outreachPriority";

const ATTEMPT_TYPES = ["teams", "email"];
const ATTEMPT_TYPE_LABELS = { teams: "MS Teams", email: "Email" };
const ATTEMPT_TYPE_ITEMS = ATTEMPT_TYPES.map((t) => ({ value: t, label: ATTEMPT_TYPE_LABELS[t] }));
const MAX_ATTEMPTS = 3;
// Reserved for the cron job's automatic archiving; checked client-side for
// fast feedback, backend re-validates.
const RESERVED_ARCHIVE_REASONS = new Set(["launched", "out_of_attempts"]);

const FEEDBACK_PAGE_SIZE = 20;

const EMPTY_ATTEMPT_FORM = {
  attempt_type: ATTEMPT_TYPES[0],
  provider_feedback: "",
  attempt_note: "",
};

const SummaryField = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 10, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 13, fontWeight: 500, color: T.text, whiteSpace: "pre-wrap" }}>{value}</div>
  </div>
);

export default function OutreachDetail({ doctorUserId, onBack, showToast }) {
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [candidate, setCandidate] = useState(null);
  const [attempts, setAttempts] = useState([]);

  const [attemptForm, setAttemptForm] = useState(EMPTY_ATTEMPT_FORM);
  const [submittingAttempt, setSubmittingAttempt] = useState(false);

  // Inline edit state for one attempt row at a time; only provider_feedback
  // is editable here.
  const [editingAttemptNumber, setEditingAttemptNumber] = useState(null);
  const [attemptEditDraft, setAttemptEditDraft] = useState({ provider_feedback: "" });
  const [savingAttemptEdit, setSavingAttemptEdit] = useState(false);

  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [removeReason, setRemoveReason] = useState("");
  const [removeError, setRemoveError] = useState("");
  const [removingCandidate, setRemovingCandidate] = useState(false);

  const [feedbackPanelOpen, setFeedbackPanelOpen] = useState(false);
  const [userFeedback, setUserFeedback] = useState(null);
  const [feedbackLoading, setFeedbackLoading] = useState(true);
  const [feedbackError, setFeedbackError] = useState("");
  const [loadingMoreFeedback, setLoadingMoreFeedback] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    setFeedbackLoading(true);
    setFeedbackError("");
    setFeedbackPanelOpen(false);
    try {
      const [detailOutcome, feedbackOutcome] = await Promise.allSettled([
        fetchOutreachDetail(doctorUserId),
        fetchOutreachUserFeedback(doctorUserId, { limit: FEEDBACK_PAGE_SIZE, offset: 0 }),
      ]);

      if (detailOutcome.status === "rejected") {
        const err = detailOutcome.reason;
        if (err.status === 404) {
          setNotFound(true);
        } else {
          console.error("Failed to fetch outreach detail:", err);
          showToast?.("Failed to load provider details");
        }
        setCandidate(null);
        setAttempts([]);
      } else {
        setCandidate(detailOutcome.value?.candidate || null);
        setAttempts(detailOutcome.value?.attempts || []);
      }

      if (feedbackOutcome.status === "fulfilled") {
        setUserFeedback(feedbackOutcome.value);
      } else {
        const err = feedbackOutcome.reason;
        if (err.status !== 404) {
          console.error("Failed to fetch user feedback:", err);
          setFeedbackError(err.message || "Failed to load user feedback");
        }
        setUserFeedback(null);
      }
    } finally {
      setLoading(false);
      setFeedbackLoading(false);
    }
  }, [doctorUserId, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  // Attempt array length is the source of truth for the cap (can't drift
  // like a separate count field would).
  const attemptLimitReached = attempts.length >= MAX_ATTEMPTS;
  const isRemoved = !!candidate?.removed_at;

  const submitAttempt = async () => {
    if (attemptLimitReached || submittingAttempt) return;
    setSubmittingAttempt(true);
    try {
      const res = await logOutreachAttempt(doctorUserId, attemptForm);
      setAttempts((prev) => [...prev, res.data]);
      setCandidate((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          tracking_note: res.tracking_note !== undefined ? res.tracking_note : prev.tracking_note,
          attempt_count: res.attempt_count ?? prev.attempt_count,
          priority: res.priority !== undefined ? res.priority : prev.priority,
          reason_for_archive: res.reason_for_archive !== undefined ? res.reason_for_archive : prev.reason_for_archive,
          request_updated_at: res.request_updated_at !== undefined ? res.request_updated_at : prev.request_updated_at,
        };
      });
      setAttemptForm(EMPTY_ATTEMPT_FORM);
      showToast?.("Attempt logged");
    } catch (err) {
      if (err.status === 409 || err.status === 404) {
        showToast?.(err.message);
        if (err.status === 404) setNotFound(true);
      } else {
        console.error("Failed to log outreach attempt:", err);
        showToast?.("Failed to log attempt");
      }
    } finally {
      setSubmittingAttempt(false);
    }
  };

  const startEditAttempt = (a) => {
    setEditingAttemptNumber(a.attempt_number);
    setAttemptEditDraft({ provider_feedback: a.provider_feedback || "" });
  };

  const cancelEditAttempt = () => {
    setEditingAttemptNumber(null);
  };

  const saveAttemptEdit = async (attemptNumber) => {
    if (savingAttemptEdit) return;
    setSavingAttemptEdit(true);
    try {
      const res = await updateOutreachAttempt(doctorUserId, attemptNumber, attemptEditDraft);
      setAttempts((prev) => prev.map((a) => (a.attempt_number === attemptNumber ? res.data : a)));
      setEditingAttemptNumber(null);
      showToast?.("Attempt updated");
    } catch (err) {
      if (err.status === 404) {
        showToast?.(err.message);
        setNotFound(true);
      } else {
        console.error("Failed to update outreach attempt:", err);
        showToast?.("Failed to update attempt");
      }
    } finally {
      setSavingAttemptEdit(false);
    }
  };

  const openFeedbackPanel = () => setFeedbackPanelOpen(true);
  const closeFeedbackPanel = () => setFeedbackPanelOpen(false);

  const loadMoreFeedback = async () => {
    if (loadingMoreFeedback || !userFeedback?.has_more) return;
    setLoadingMoreFeedback(true);
    try {
      const res = await fetchOutreachUserFeedback(doctorUserId, {
        limit: FEEDBACK_PAGE_SIZE,
        offset: userFeedback.entries.length,
      });
      setUserFeedback((prev) => (prev ? {
        ...prev,
        entries: [...prev.entries, ...(res.entries || [])],
        has_more: res.has_more,
      } : prev));
    } catch (err) {
      console.error("Failed to fetch more user feedback:", err);
      showToast?.("Failed to load more feedback");
    } finally {
      setLoadingMoreFeedback(false);
    }
  };

  const openRemoveModal = () => {
    setRemoveReason("");
    setRemoveError("");
    setRemoveModalOpen(true);
  };

  const closeRemoveModal = () => {
    if (removingCandidate) return;
    setRemoveModalOpen(false);
  };

  const submitRemoveCandidate = async () => {
    const reason = removeReason.trim();
    if (!reason) {
      setRemoveError("A reason for removal is required");
      return;
    }
    if (RESERVED_ARCHIVE_REASONS.has(reason.toLowerCase())) {
      setRemoveError(`"${reason}" is reserved for automatic archiving — please describe the actual reason.`);
      return;
    }
    if (removingCandidate) return;
    setRemovingCandidate(true);
    try {
      const res = await removeOutreachCandidate(doctorUserId, reason);
      setCandidate((prev) => (prev ? { ...prev, removed_at: res.data.removed_at, reason_for_archive: res.data.reason_for_archive, priority: res.data.priority } : prev));
      setEditingAttemptNumber(null);
      setRemoveModalOpen(false);
      showToast?.("Candidate removed");
    } catch (err) {
      if (err.status === 400 || err.status === 409 || err.status === 404) {
        // Show inline in the modal; keep the loaded candidate visible.
        setRemoveError(err.message);
      } else {
        console.error("Failed to remove outreach candidate:", err);
        setRemoveError("Failed to remove candidate");
      }
    } finally {
      setRemovingCandidate(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
        <LoadingAnimation size={28} />
      </div>
    );
  }

  if (notFound) {
    return (
      <>
        <div style={{ marginBottom: 20 }}>
          <Button variant="text" icon={ArrowLeft} onClick={onBack}>Back to Outreach</Button>
        </div>
        <Banner variant="warning" heading="Provider not found">
          This provider may have been removed or the link is stale.
        </Banner>
      </>
    );
  }

  const priorityMeta = candidate ? outreachStatusMetaFor(candidate) : null;
  const isTierLabel = candidate && !isRemoved && candidate.priority != null;

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <Button variant="text" icon={ArrowLeft} onClick={onBack}>Back to Outreach</Button>
        {isRemoved ? (
          <Badge variant="default">Removed</Badge>
        ) : (
          candidate && <Button variant="secondary-danger" onClick={openRemoveModal}>Remove Candidate</Button>
        )}
      </div>

      <div style={feedbackPanelOpen ? {
        display: "flex",
        alignItems: "stretch",
        height: "calc(100vh - 160px)",
        overflow: "hidden",
        gap: 16,
      } : undefined}>
      <div style={feedbackPanelOpen ? { flex: 1, minWidth: 0, overflowY: "auto" } : undefined}>

      {isRemoved && (
        <div style={{ marginBottom: 20 }}>
          <Banner variant="info">
            This candidate has been removed. It's shown here read-only for reference.
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 16, marginTop: 14 }}>
              <div>
                <BodyText size="small" muted>Removed At</BodyText>
                <BodyText size="small">{fmtDate(candidate?.removed_at)}</BodyText>
              </div>
              <div>
                <BodyText size="small" muted>Reason</BodyText>
                <BodyText size="small">{
                  candidate?.reason_for_archive === "out_of_attempts" ? "Out of attempts"
                    : candidate?.reason_for_archive === "launched" ? "Launched"
                    : candidate?.reason_for_archive || "—"
                }</BodyText>
              </div>
            </div>
          </Banner>
        </div>
      )}

      {/* Summary */}
      {candidate ? (
        <div style={{ marginBottom: 20 }}>
        <Card variant="elevated">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18, gap: 12, flexWrap: "wrap" }}>
            <CardHeader>
              <CardTitle>{candidate.provider_name}</CardTitle>
              <BodyText size="small" muted>{candidate.email}</BodyText>
            </CardHeader>
            {priorityMeta && (
              <Badge variant={priorityMeta.badge}>{isTierLabel ? `Tier ${candidate.priority}` : priorityMeta.label}</Badge>
            )}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 16 }}>
            {[
              { label: "Station", value: `${candidate.station_id || "—"} — ${candidate.station_name || ""}` },
              { label: "VISN", value: candidate.visn ?? "—" },
              { label: "Specialty", value: candidate.specialty || "—" },
              { label: "Date Activated", value: fmtDate(candidate.date_activated) },
              { label: "Date Launched", value: fmtDate(candidate.date_launched) },
              { label: "Days Idle", value: candidate.days_idle ?? "—" },
              { label: "Attempts Logged", value: `${attempts.length} / ${MAX_ATTEMPTS}` },
              ...(candidate.reason_for_archive ? [{
                label: "Archive Reason",
                value: candidate.reason_for_archive === "out_of_attempts" ? "Out of attempts"
                  : candidate.reason_for_archive === "launched" ? "Launched"
                  : candidate.reason_for_archive,
              }] : []),
              ...(candidate.request_updated_at ? [{ label: "Last Updated", value: fmtDate(candidate.request_updated_at) }] : []),
            ].map(({ label, value }) => (
              <SummaryField key={label} label={label} value={value} />
            ))}
          </div>
          <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 18, paddingTop: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
              <CardTitle>Provider Feedback</CardTitle>
              {userFeedback?.rating_count > 1 && (
                <Button variant="secondary" onClick={openFeedbackPanel}>View more</Button>
              )}
            </div>
            {feedbackLoading ? (
              <div style={{ display: "flex", justifyContent: "center", padding: "12px 0" }}>
                <LoadingAnimation size={22} />
              </div>
            ) : feedbackError ? (
              <Banner variant="warning" heading="Unable to load feedback">
                {feedbackError}
              </Banner>
            ) : !userFeedback || userFeedback.rating_count === 0 ? (
              <BodyText size="small" muted>No user feedback has been submitted for this provider.</BodyText>
            ) : (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 16, marginBottom: 14 }}>
                  <SummaryField
                    label="Most Recent"
                    value={userFeedback.last_rating != null
                      ? `${userFeedback.last_rating_date ? `${fmtDate(userFeedback.last_rating_date)} - ` : ""}${userFeedback.last_rating}`
                      : "—"}
                  />
                  <SummaryField
                    label="Avg. Rating"
                    value={userFeedback.avg_rating != null ? Number(userFeedback.avg_rating).toFixed(2) : "—"}
                  />
                  <SummaryField
                    label="Patient ID"
                    value={userFeedback.last_patient_id || "—"}
                  />
                </div>
                <SummaryField
                  label="Recent Feedback"
                  value={userFeedback.feedback_message || "—"}
                />
              </>
            )}
          </div>
        </Card>
        </div>
      ) : (
        <div style={{ marginBottom: 20, fontStyle: "italic" }}>
          <BodyText size="small" muted>This provider is no longer an active outreach candidate — showing attempt history below.</BodyText>
        </div>
      )}

      {/* Attempt history */}
      <div style={{ marginBottom: 20 }}>
      <Card variant="elevated" size="small">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: 14, borderBottom: `1px solid ${T.border}`, marginBottom: 4 }}>
          <Heading as="h3" size="x-small">Attempt History</Heading>
          <span style={{ fontSize: 12, color: T.textMuted }}>{attempts.length} of {MAX_ATTEMPTS}</span>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {["#", "Type", "Logged At", "Provider Feedback", "Note", "Logged By", ""].map((h) => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {attempts.map((a) => {
              const isEditing = editingAttemptNumber === a.attempt_number;
              return (
                <tr key={a.attempt_number}>
                  <td style={S.td}>{a.attempt_number}</td>
                  <td style={S.td}><Badge variant="default">{ATTEMPT_TYPE_LABELS[a.attempt_type] || a.attempt_type}</Badge></td>
                  <td style={S.td}>{fmtDate(a.attempt_created_at)}</td>
                  <td style={{ ...S.td, ...(isEditing ? {} : S.muted), maxWidth: 220 }}>
                    {isEditing ? (
                      <FieldText
                        size="small"
                        placeholder="e.g. No answer, left voicemail"
                        value={attemptEditDraft.provider_feedback}
                        onChange={(e) => setAttemptEditDraft((d) => ({ ...d, provider_feedback: e.target.value }))}
                        aria-label="Provider feedback"
                      />
                    ) : (a.provider_feedback || "—")}
                  </td>
                  <td style={{ ...S.td, ...S.muted, maxWidth: 220 }}>{a.attempt_note || "—"}</td>
                  <td style={S.td}>{a.attempt_by || "—"}</td>
                  <td style={{ ...S.td, whiteSpace: "nowrap" }}>
                    {isRemoved ? null : isEditing ? (
                      <div style={{ display: "flex", gap: 6 }}>
                        <Button
                          variant="text"
                          size="small"
                          disabled={savingAttemptEdit}
                          onClick={() => saveAttemptEdit(a.attempt_number)}
                          icon={FloppyDisk}
                          aria-label="Save"
                        />
                        <Button
                          variant="text"
                          size="small"
                          disabled={savingAttemptEdit}
                          onClick={cancelEditAttempt}
                          icon={Xmark}
                          aria-label="Cancel"
                        />
                      </div>
                    ) : (
                      <Button variant="text" size="small" onClick={() => startEditAttempt(a)} icon={EditPencil} aria-label="Edit" />
                    )}
                  </td>
                </tr>
              );
            })}
            {attempts.length === 0 && (
              <tr><td colSpan={7} style={{ ...S.td, textAlign: "center", color: T.textDim }}>No attempts logged yet</td></tr>
            )}
          </tbody>
        </table>
      </Card>
      </div>

      {/* Log attempt */}
      <div style={{ marginBottom: 20 }}>
      <Card variant="elevated">
        <Heading as="h3" size="x-small">Log Attempt</Heading>
        {isRemoved ? (
          <div style={{ marginTop: 12 }}>
            <Banner variant="info">This candidate has been removed — no further attempts can be logged.</Banner>
          </div>
        ) : attemptLimitReached ? (
          <div style={{ marginTop: 12 }}>
            <Banner variant="warning">{attempts.length} of {MAX_ATTEMPTS} attempts logged — no further attempts can be logged for this provider.</Banner>
          </div>
        ) : (
          <div style={{ marginTop: 12 }}>
          <Form onSubmit={submitAttempt} loading={submittingAttempt}>
            <div style={{ maxWidth: 220 }}>
              {/* FieldSelect takes no label prop, so the title is rendered
                  here matching the library's own field-label styling. */}
              <div style={{ fontSize: 13, color: "#374151", marginBottom: 6 }}>Type</div>
              <FieldSelect
                items={ATTEMPT_TYPE_ITEMS}
                value={attemptForm.attempt_type}
                onChange={(v) => setAttemptForm((f) => ({ ...f, attempt_type: v }))}
                size="small"
                aria-label="Type"
              />
            </div>
            <FieldTextarea
              label="Notes"
              placeholder="Any additional context about this attempt..."
              value={attemptForm.attempt_note}
              onChange={(e) => setAttemptForm((f) => ({ ...f, attempt_note: e.target.value }))}
              rows={3}
              size="small"
            />
            <FieldText
              label="Provider Feedback"
              placeholder="e.g. No answer, left voicemail"
              value={attemptForm.provider_feedback}
              onChange={(e) => setAttemptForm((f) => ({ ...f, provider_feedback: e.target.value }))}
              size="small"
            />
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button variant="primary" type="submit" loading={submittingAttempt}>
                {submittingAttempt ? "Logging..." : "Log Attempt"}
              </Button>
            </div>
          </Form>
          </div>
        )}
      </Card>
      </div>
      </div>

      {feedbackPanelOpen && (
      <div style={{
        width: "32%",
        flexShrink: 0,
        minHeight: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: T.radius,
        boxShadow: T.shadow,
        overflow: "hidden",
        boxSizing: "border-box",
        padding: 20,
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          paddingBottom: 12,
          marginBottom: 12,
          borderBottom: `1px solid ${T.border}`,
          flexShrink: 0,
        }}>
          <CardTitle>Provider Feedback</CardTitle>
          <Button variant="text" size="small" icon={Xmark} onClick={closeFeedbackPanel} aria-label="Close">
            Close
          </Button>
        </div>
        <div style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
          {feedbackLoading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
              <LoadingAnimation size={28} />
            </div>
          ) : feedbackError ? (
            <Banner variant="warning" heading="Unable to load feedback">
              {feedbackError}
            </Banner>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              {(userFeedback?.entries || []).map((entry, index) => (
                <div
                  key={`${entry.appt_date || "row"}-${index}`}
                  style={{ paddingBottom: 16, borderBottom: index < (userFeedback.entries.length - 1) ? `1px solid ${T.border}` : "none" }}
                >
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                    <SummaryField label="Date" value={fmtDate(entry.appt_date)} />
                    <SummaryField label="Rating" value={entry.rating != null ? entry.rating : "—"} />
                    <SummaryField label="Patient ID" value={entry.patient_id || "—"} />
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <SummaryField label="Feedback" value={entry.feedback || "—"} />
                  </div>
                </div>
              ))}
              {userFeedback?.has_more && (
                <div style={{ display: "flex", justifyContent: "center", paddingTop: 4 }}>
                  <Button variant="secondary" size="small" onClick={loadMoreFeedback} loading={loadingMoreFeedback}>
                    {loadingMoreFeedback ? "Loading..." : "Load more"}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      )}
      </div>

      {/* Remove Candidate modal */}
      <ConfirmationModal
        open={removeModalOpen}
        title="Remove Candidate"
        continueText={removingCandidate ? "Removing..." : "Remove"}
        cancelText="Cancel"
        continueVariant="primary-danger"
        onContinue={submitRemoveCandidate}
        onCancel={closeRemoveModal}
      >
        <BodyText size="small" muted>
          This marks the request as removed and can't be undone here. A reason is required.
        </BodyText>
        <div style={{ marginTop: 12 }}>
          <FieldTextarea
            label="Reason for removal"
            placeholder="Why is this candidate being removed?"
            value={removeReason}
            onChange={(e) => {
              setRemoveReason(e.target.value);
              if (removeError) setRemoveError("");
            }}
            error={removeError || undefined}
            rows={3}
            autoFocus
          />
        </div>
      </ConfirmationModal>
    </>
  );
}
